import { createRef, ReactNode, useCallback, useEffect, useState } from 'react';
import Chevron from '../components/icons/Chevron';
import Alert from '../components/icons/Alert';
import { Loader2Icon } from 'lucide-react';
import { Input } from '@/lib/components/ui/input';

const keyExtractor = (item: any) => item?.key ?? item?.Id;

type ComboBoxTypes<OptionT> = {
  formatOptionLabel: (option: OptionT) => ReactNode;
  getOptionLabel: (option: OptionT) => string;
  isLoading: boolean;
  onChange: (option: OptionT | null) => void;
  options: OptionT[];
  label: string;
  value?: OptionT | null;
  filterOption?: (option: OptionT, query: string) => boolean;
  disabled?: boolean;
  open?: boolean;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  description?: ReactNode;
  error?: string;
};

const ComboBox = <OptionT extends unknown>({
  disabled,
  filterOption,
  formatOptionLabel,
  getOptionLabel,
  isLoading,
  onChange,
  options,
  label,
  value,
  open = false,
  onInputChange,
  placeholder,
  description,
  error,
}: ComboBoxTypes<OptionT>) => {
  const [items, setItems] = useState<OptionT[]>(options);
  const [selectedItem, setSelectedItem] = useState(value);
  const [isOpen, setIsOpen] = useState(open);
  const [inputValue, setInputValue] = useState(
    value ? getOptionLabel(value) : '',
  );
  const inputRef = createRef<HTMLInputElement>();

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    setItems(options);
  }, [options]);

  useEffect(() => {
    if (value === selectedItem) return;
    setItem(value);
  }, [value, isLoading]);

  const getItemIndex = useCallback(
    (item?: OptionT | null) =>
      item
        ? items.findIndex(
            (option) => getOptionLabel(option) === getOptionLabel(item),
          )
        : -1,
    [items, getOptionLabel],
  );

  const setItem = useCallback(
    (item: OptionT | null | undefined) => {
      setInputValue(item ? getOptionLabel(item) : '');
      setSelectedItem(item);
    },
    [setSelectedItem, setInputValue, getOptionLabel],
  );

  const onSelectItem = useCallback(
    (item: OptionT) => {
      onChange(item);
      setItem(item);
      setIsOpen(false);
    },
    [onChange],
  );

  const onInputValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.value === '') {
        reset();
      } else if (filterOption) {
        setItems(
          options.filter((option) =>
            filterOption(option, e.currentTarget.value),
          ),
        );
      }
      if (onInputChange) {
        onInputChange(e.currentTarget.value);
      }
      setInputValue(e.currentTarget.value);

      setIsOpen(true);
    },
    [options, filterOption, onInputChange],
  );

  const reset = useCallback(() => {
    setItems([]);
    setItem(null);
    onChange(null);
  }, []);

  const onInputBlur = useCallback(() => {
    if (inputValue === '') {
      reset();
    } else if (selectedItem && getOptionLabel(selectedItem) !== inputValue) {
      onSelectItem(selectedItem);
    }
    setIsOpen(false);
  }, [inputValue, selectedItem, getOptionLabel]);

  const onInputFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onButtonClick = useCallback(() => {
    inputRef.current?.focus();
    setIsOpen(!isOpen);
  }, [inputRef, isOpen]);

  const onMouseDownPrevent = useCallback(
    (e: React.MouseEvent<HTMLLIElement | HTMLButtonElement>) => {
      e.preventDefault();
    },
    [],
  );

  const onItemClick = useCallback(
    (item: OptionT) => () => onSelectItem(item),
    [onSelectItem],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Escape':
          // Make sure to stop propagation to prevent closing the dialog
          e.stopPropagation();
          // Prevent default to make sure the input doesn't lose focus
          e.preventDefault();
          if (!isOpen || !items.length) {
            reset();
          } else if (selectedItem) {
            onSelectItem(selectedItem);
          }
          setIsOpen(false);
          break;
        case 'Enter':
          if (isOpen) {
            if (selectedItem) {
              onSelectItem(selectedItem);
            }
            setIsOpen(false);
          } else {
            setIsOpen(true);
          }
          break;
        case 'ArrowDown': {
          if (items.length === 0) return;
          const currentIndex = getItemIndex(selectedItem);
          const nextIndex =
            currentIndex === items.length - 1 ? 0 : currentIndex + 1;

          setSelectedItem(items[nextIndex]);
          setIsOpen(true);
          break;
        }
        case 'ArrowUp': {
          if (items.length === 0) return;
          const currentIndex = getItemIndex(selectedItem);
          const prevIndex =
            currentIndex <= 0 ? items.length - 1 : currentIndex - 1;

          setSelectedItem(items[prevIndex]);
          setIsOpen(true);
          break;
        }
        default:
      }
    },
    [isOpen, items, selectedItem, onSelectItem],
  );

  return (
    <div className="relative w-full">
      <div className={`flex flex-col gap-1`}>
        <label htmlFor={label} className={`block text-base font-bold mb-2`}>
          {label}
        </label>
        <div
          className={`flex items-center gap-0.5 border rounded-md justify-center focus:outline-0 ${
            disabled
              ? 'bg-neutral-300 border-neutral-300'
              : error
                ? 'bg-white  border-destructive'
                : isOpen
                  ? 'bg-white border-samsungBlue'
                  : 'bg-white border-neutral-400'
          }`}
        >
          <Input
            id={label}
            value={inputValue}
            required
            placeholder={placeholder}
            aria-expanded={Boolean(isOpen && items.length)}
            aria-activedescendant={keyExtractor(selectedItem) || ''}
            aria-busy={isLoading}
            aria-controls="toggle-menu"
            disabled={disabled}
            role="combobox"
            onFocus={onInputFocus}
            ref={inputRef}
            onChange={onInputValueChange}
            onBlur={onInputBlur}
            onClick={onInputFocus}
            onKeyDown={onKeyDown}
            autoComplete="off"
            className={'p-2 w-full text-base outline-hidden bg-white/0'}
          />
          {isLoading && <Loader2Icon className="animate-spin mx-2" />}
          {items.length > 0 && (
            <button
              id="toggle-menu"
              aria-label="toggle menu"
              tabIndex={-1}
              className="mx-2 w-7 h-7"
              disabled={disabled}
              type="button"
              onClick={onButtonClick}
              onMouseDown={onMouseDownPrevent}
            >
              <Chevron rotate={Boolean(isOpen && items.length) ? 0 : 180} />
            </button>
          )}
        </div>
      </div>
      <ul
        role="listbox"
        className={`absolute w-full bg-white mt-1 shadow-md rounded-md border border-gray-200 max-h-80 overflow-scroll p-0 z-1 ${
          !(isOpen && items.length) && 'hidden'
        }`}
      >
        {isOpen &&
          items.map((item, index) => {
            const isSelected =
              selectedItem &&
              getOptionLabel(selectedItem) === getOptionLabel(item);

            return (
              <li
                id={keyExtractor(item)}
                key={keyExtractor(item)}
                tabIndex={index + 1}
                aria-selected={Boolean(isSelected)}
                role="option"
                className={`
                ${isSelected && 'bg-neutral-100'}
                py-2 px-3 shadow-xs flex flex-row justify-between cursor-pointer hover:bg-neutral-100`}
                onMouseDown={onMouseDownPrevent}
                onClick={onItemClick(item)}
                ref={(el) => {
                  if (isSelected && el) {
                    el.scrollIntoView();
                  }
                }}
              >
                {formatOptionLabel(item)}
              </li>
            );
          })}
      </ul>
      {error && (
        <p className="flex items-center text-sm text-destructive mt-1">
          <Alert className="w-5 h-5 sm:w-3 sm:h-3 mr-1" /> {error}
        </p>
      )}
      {description && <div className="mt-4">{description}</div>}
    </div>
  );
};

export default ComboBox;
